// src/Pages/WhoPage.jsx

import React, { useState, useEffect } from 'react';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faOctagonCheck } from '@fortawesome/pro-regular-svg-icons';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  introSection: {
    padding: '150px 20px',
    backgroundColor: '#ff2300',
    textAlign: 'center',
  },
  introTitle: {
    fontSize: '60px',
    color: '#fff',
    marginBottom: '20px',
    fontWeight: '700',
    lineHeight: '1.1',
  },
  introText: {
    fontSize: '18px',
    fontWeight: '400',
    color: '#fff',
    lineHeight: '1.6',
    maxWidth: '800px',
    margin: '0 auto',
  },
  mainSection: {
    padding: '50px 20px',
    backgroundColor: '#f9f9f9',
    flex: '1',
  },
  section: {
    maxWidth: '900px',
    margin: '0 auto 60px auto',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: '32px',
    color: '#222',
    marginBottom: '16px',
    fontWeight: '600',
  },
  sectionText: {
    fontSize: '18px',
    color: '#333',
    lineHeight: '1.7',
    marginBottom: '20px',
    fontWeight: '500',
    whiteSpace: 'pre-wrap',
  },
  bulletList: {
    listStyle: 'none',
    paddingLeft: 0,
    fontSize: '17px',
    color: '#333',
    fontWeight: '500',
    textAlign: 'left',
    maxWidth: '600px',
    margin: '0 auto',
  },
  bulletItem: {
    marginBottom: '10px',
    position: 'relative',
    paddingLeft: '28px', // espacio para el icono
    display: 'flex',
    alignItems: 'center',
  },
  locationsSection: {
    padding: '80px 20px',
    backgroundColor: '#fff',
    textAlign: 'center',
  },
  locationsTitle: {
    fontSize: '48px',
    color: '#222',
    marginBottom: '40px',
    fontWeight: '700',
  },
  locationsGrid: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '80px',
    marginTop: '40px',
  },
  locationCard: {
    flex: '1 1 400px',
    maxWidth: '400px',
    textAlign: 'left',
  },
  locationContentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: '24px',
    color: '#111',
    marginBottom: '8px',
    fontWeight: '600',
    textAlign: 'center',
  },
  locationAddress: {
    fontSize: '16px',
    color: '#333',
    lineHeight: '1.5',
    fontWeight: '400',
    textAlign: 'center',
  },
  mapContainer: {
    flex: 1,
    height: '200px',
    borderRadius: '8px',
    overflow: 'hidden',
  },
};

const content = {
  es: {
    sections: [
      {
        key: 'intro',
        title: 'Personas excepcionales. Un mismo propósito.',
        text: 'Un equipo con experiencia global, visión compartida y compromiso con el impacto real.',
      },
      {
        key: 'history',
        title: 'Nuestra historia',
        text: `Venimos de grandes compañías tecnológicas, de sectores globales como software, formación, innovación, cloud, operaciones y diseño.

Hemos liderado transformaciones, lanzado productos, formado a miles de personas y escalado soluciones tecnológicas en organizaciones líderes.

Hoy, unimos todo ese conocimiento para construir algo diferente: herramientas que realmente ayudan a las personas a crecer.`,
        image: 'story.jpeg',
        imageAlt: 'Oficina 1 a 1',
      },
      {
        key: 'define',
        title: 'Qué nos define',
        bullets: [
          'Diversidad de experiencia: producto, tecnología, marketing, estrategia, talento y más.',
          'Pasado compartido en corporaciones globales: sabemos cómo operan y cómo transformarlas.',
          'Cultura de colaboración: creemos que el trabajo bien hecho se construye en equipo.',
          'Mentalidad ágil, corazón humano: combinamos eficiencia con empatía.',
        ],
        image: 'coworking.jpg',
        imageAlt: 'Coworking',
      },
      {
        key: 'vision',
        title: 'Nuestra visión compartida',
        text: `Creemos que el talento florece cuando se le da estructura, contexto y confianza.

Por eso creamos plataformas donde el crecimiento es posible, medible y personal.

Y lo hacemos con el mismo estándar de excelencia que aplicamos en nuestros años en grandes corporaciones… pero con la agilidad y la cercanía de un equipo que trabaja con propósito.`,
        cta: 'Descubre cómo trabajamos',
        image: '1o1.jpeg',
        imageAlt: 'Reunión 1 a 1',
      },
    ],
  },
  en: {
    sections: [
      {
        key: 'intro',
        title: 'Exceptional people. One shared purpose.',
        text: 'A team with global experience, a shared vision, and a commitment to real impact.',
      },
      {
        key: 'history',
        title: 'Our story',
        text: `We come from leading global companies across sectors like software, learning, cloud, innovation, operations, and design.

We’ve led transformations, launched products, trained thousands, and scaled tech solutions in some of the world’s most recognized organizations.

Now, we bring all that knowledge together to build something different: tools that truly help people grow.`,
        image: 'story.jpeg',
        imageAlt: 'Our Story',
      },
      {
        key: 'define',
        title: 'What defines us',
        bullets: [
          'Diverse expertise: product, tech, marketing, strategy, talent, and more.',
          'Shared background in global corporations: we know how they work—and how to transform them.',
          'Collaborative culture: great work happens when people come together.',
          'Agile mindset, human heart: we balance speed with empathy.',
        ],
        image: 'coworking.jpg',
        imageAlt: 'Coworking',
      },
      {
        key: 'vision',
        title: 'Our shared vision',
        text: `We believe talent flourishes when given structure, context, and trust.

That’s why we build platforms where growth is possible, measurable, and personal.

And we do it with the same standard of excellence we applied in our time at global companies—with the agility and care of a purpose-driven team.`,
        cta: 'See how we work',
        image: '1o1.jpeg',
        imageAlt: 'One-on-one meeting',
      },
    ],
  },
};

function WhoPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const lang =
    content[language] && content[language].sections
      ? content[language]
      : content.es;
  const sections = lang.sections;

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      {/* Introductory red section */}
      <section style={styles.introSection}>
        <h1 style={styles.introTitle}>{sections[0].title}</h1>
        {sections[0].text && <p style={styles.introText}>{sections[0].text}</p>}
      </section>

      {/* Alternating content sections */}
      <main style={styles.mainSection}>
        {sections.slice(1).map((sec, idx) => {
          if (isMobile) {
            return (
              <section
                key={sec.key}
                style={{
                  ...styles.section,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                }}
              >
                <h2 style={styles.sectionTitle}>{sec.title}</h2>
                {sec.image && (
                  <img
                    src={require(
                      `../pagecomponents/screenshots/office/${sec.image}`
                    )}
                    alt={sec.imageAlt}
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                )}
                {sec.text && <p style={styles.sectionText}>{sec.text}</p>}
                {sec.bullets && (
                  <ul style={styles.bulletList}>
                    {sec.bullets.map((item, i) => (
                      <li key={i} style={styles.bulletItem}>
                        <FontAwesomeIcon
                          icon={faOctagonCheck}
                          style={{ color: '#ff2300' }}
                        />
                        <span style={{ marginLeft: '8px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          }
          return (
            <section
              key={sec.key}
              style={{
                ...styles.section,
                display: 'flex',
                flexDirection: idx % 2 === 0 ? 'row' : 'row-reverse',
                alignItems: 'center',
                gap: '40px',
              }}
            >
              <div style={{ flex: 1, textAlign: 'left' }}>
                <h2 style={styles.sectionTitle}>{sec.title}</h2>
                {sec.text && <p style={styles.sectionText}>{sec.text}</p>}
                {sec.bullets && (
                  <ul style={styles.bulletList}>
                    {sec.bullets.map((item, i) => (
                      <li key={i} style={styles.bulletItem}>
                        <FontAwesomeIcon
                          icon={faOctagonCheck}
                          style={{ color: '#ff2300' }}
                        />
                        <span style={{ marginLeft: '8px' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {sec.image && (
                <div style={{ flex: 1 }}>
                  <img
                    src={require(
                      `../pagecomponents/screenshots/office/${sec.image}`
                    )}
                    alt={sec.imageAlt}
                    style={{ width: '100%', borderRadius: '8px' }}
                  />
                </div>
              )}
            </section>
          );
        })}
      </main>

      {/* Office Locations / Nuestras oficinas */}
      <section style={styles.locationsSection}>
        <h2 style={styles.locationsTitle}>
          {language === 'es' ? 'Nuestras oficinas' : 'Office Locations'}
        </h2>
        <div style={styles.locationsGrid}>
          {/* Barcelona */}
          <div style={styles.locationCard}>
            <div style={styles.locationContentRow}>
              <div style={styles.locationInfo}>
                <h3 style={styles.locationName}>Bether HQ</h3>
                <p style={styles.locationAddress}>
                  Carrer de Galileu, 142
                  <br />
                  Sants-Montjuïc, 08028 Barcelona
                  <br />
                  España
                </p>
              </div>
              <div style={styles.mapContainer}>
                <iframe
                  title="Bether HQ"
                  src="https://www.google.com/maps?q=Carrer%20de%20Galileu%20142%2C%20Sants-Montju%C3%AFc%2C%2008028%20Barcelona&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
          {/* Madrid */}
          <div style={styles.locationCard}>
            <div style={styles.locationContentRow}>
              <div style={styles.locationInfo}>
                <h3 style={styles.locationName}>
                  {language === 'es' ? 'Oficina Madrid' : 'Madrid Office'}
                </h3>
                <p style={styles.locationAddress}>
                  Calle de María de Molina, 39, 3ª planta
                  <br />
                  Chamartín, 28006 Madrid
                  <br />
                  España
                </p>
              </div>
              <div style={styles.mapContainer}>
                <iframe
                  title="Madrid Office"
                  src="https://www.google.com/maps?q=Calle%20de%20Mar%C3%ADa%20de%20Molina%2039%203%C2%AA%20planta%2C%20Chamart%C3%ADn%2C%2028006%20Madrid&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default WhoPage;
